import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import PageHeader from '../../components/PageHeader';
import ScoreCard from '../../components/ScoreCard';
import ScoreDisclaimer from '../../components/ScoreDisclaimer';
import ScoreTrendChart from '../../components/ScoreTrendChart';
import LoadingSpinner from '../../components/LoadingSpinner';
import { clientApi } from '../../services/client';
import { scoringApi } from '../../services/scoring';
import { toGGScore, toGGDelta, getScoreColor, getScoreColorClass } from '../../utils/score';

export default function ClientDashboard() {
  const navigate = useNavigate();

  const { data: org, isLoading: orgLoading } = useQuery({
    queryKey: ['client', 'organization'],
    queryFn: () => clientApi.getOrganization().then((r) => r.data),
  });

  const { data: sites } = useQuery({
    queryKey: ['client', 'sites', 'summary'],
    queryFn: () => clientApi.getSites({ page: '1' }).then((r) => r.data),
  });

  const { data: vendors } = useQuery({
    queryKey: ['client', 'vendors', 'summary'],
    queryFn: () => clientApi.getVendors({ page: '1' }).then((r) => r.data),
  });

  // Show trend chart for the first site (primary site)
  const firstSiteId = sites?.results?.[0]?.id;
  const { data: siteScoreHistory } = useQuery({
    queryKey: ['client', 'site-score-history', firstSiteId],
    queryFn: () => scoringApi.getClientScoreHistory('site', firstSiteId!),
    enabled: !!firstSiteId,
  });

  const { data: recentIssues } = useQuery({
    queryKey: ['client', 'recent-issues'],
    queryFn: () => clientApi.getRecentIssues(5).then((r) => r.data),
  });

  if (orgLoading) {
    return (
      <div className="flex justify-center py-12">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  const totalSites = sites?.count ?? 0;
  const totalVendors = vendors?.count ?? 0;

  return (
    <>
      <PageHeader
        title="Dashboard"
        description="Overview of your security operations"
      />

      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
        <div className="rounded-2xl border border-slate-100 bg-white p-4">
          <p className="text-sm font-medium text-slate-500">Total Sites</p>
          <p className="mt-2 text-3xl font-bold text-slate-900">{totalSites}</p>
          <p className="mt-1 text-xs text-slate-400">Sites under management</p>
        </div>
        <div className="rounded-2xl border border-slate-100 bg-white p-4">
          <p className="text-sm font-medium text-slate-500">Active Vendors</p>
          <p className="mt-2 text-3xl font-bold text-slate-900">{totalVendors}</p>
          <p className="mt-1 text-xs text-slate-400">Assigned security vendors</p>
        </div>
        <div className="rounded-2xl border border-slate-100 bg-white p-4">
          <p className="text-sm font-medium text-slate-500">Organization</p>
          <p className="mt-2 text-3xl font-bold text-slate-900">{org?.name ?? '—'}</p>
          <p className="mt-1 text-xs text-slate-400">{org?.city && org?.state ? `${org.city}, ${org.state}` : 'Client organization'}</p>
        </div>
      </div>

      {/* Vendor Scores + Your Vendor Card */}
      {vendors && vendors.results.length > 0 && (
        <div className="mb-8">
          <h2 className="text-lg font-semibold text-slate-900 mb-4">Your Vendors</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {vendors.results.map((v) => (
              <ScoreCard key={v.id} label={v.name} score={v.score} />
            ))}
          </div>
          {/* Feature 10: spec-locked legal disclaimer */}
          <div className="mt-4">
            <ScoreDisclaimer variant="short" />
          </div>
        </div>
      )}

      {/* Rich Site Cards */}
      {sites && sites.results.length > 0 && (
        <div className="mb-8">
          <h2 className="text-lg font-semibold text-slate-900 mb-4">Your Sites</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {sites.results.map((site) => (
              <button
                key={site.id}
                onClick={() => navigate(`/client/sites/${site.id}`)}
                className="text-left rounded-2xl border border-slate-100 bg-white p-5 hover:shadow-md transition-shadow"
              >
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <h3 className="text-sm font-semibold text-slate-900">{site.name}</h3>
                    <p className="text-xs text-slate-400">{site.city}, {site.state}</p>
                  </div>
                  <span className={`text-lg font-bold ${getScoreColorClass(getScoreColor(site.score))}`}>
                    {toGGScore(site.score)}
                  </span>
                </div>
                <div className="flex items-center gap-4 text-xs text-slate-500">
                  <span>{site.guard_count} guard{site.guard_count !== 1 ? 's' : ''}</span>
                  {site.vendor_org_name && <span>Vendor: {site.vendor_org_name}</span>}
                </div>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Site Score Trend */}
      <div className="rounded-2xl border border-slate-100 bg-white p-6 mb-8">
        <h2 className="text-lg font-semibold text-slate-900 mb-4">
          {sites?.results?.[0]?.name ? `${sites.results[0].name} — Score Trend` : 'Site Score Trend'}
        </h2>
        <ScoreTrendChart snapshots={siteScoreHistory?.results ?? []} />
      </div>

      {/* Recent Issues Feed */}
      <div className="rounded-2xl border border-slate-100 bg-white p-6">
        <h2 className="text-lg font-semibold text-slate-900 mb-4">Recent Issues</h2>
        {recentIssues && recentIssues.length > 0 ? (
          <div className="space-y-3">
            {recentIssues.map((issue) => (
              <div key={issue.id} className="flex items-center justify-between p-3 rounded-xl bg-slate-50">
                <div>
                  <p className="text-sm font-medium text-slate-900">{issue.rule_name}</p>
                  <p className="text-xs text-slate-500">{issue.entity_name} &middot; {issue.rule_category}</p>
                </div>
                <div className="text-right">
                  <span className={`text-sm font-bold ${issue.score_impact >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                    {issue.score_impact >= 0 ? '+' : ''}{toGGDelta(issue.score_impact)}
                  </span>
                  <p className="text-xs text-slate-400">{new Date(issue.created_at).toLocaleDateString()}</p>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-sm text-slate-400">No recent issues for your sites.</p>
        )}
      </div>
    </>
  );
}
